import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Drawer, Backdrop, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider } from '@mui/material';
import { AutoAwesome, Info, InfoOutlined } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../components';
import { RadioForm } from '../components/RadioForm';
import InfoHeader from '../components/InfoHeader';
import ReactPlayer from 'react-player';
import Empresa from '../types/negocio/Empresa';
import { useAuth } from '../context/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import toastService from '../utils/toastService';
import LoadingIndicator from '../components/LoadingIndicator';
import BasicModal from '../components/modais/BasicModal';
import { empresaService } from '../services/EmpresaService';
import SelectFundamentosModal from '../components/modais/SelectFundamentosModal';
import { 
  FundamentosType, 
  FundamentosAnswers, 
  GeneratedOption,
  FundamentosQuestions 
} from '../types/fundamentos/FundamentosTypes';
import fundamentosQuestionsData from '../config/fundamentosQuestions.json';

const Fundamentos: React.FC = () => {
  const [empresa, setEmpresa] = useState<Empresa>({} as Empresa);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [currentExamples, setCurrentExamples] = useState<{ title: string; content: React.ReactNode }>();
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false, false]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  
  // Novos estados para o fluxo de geração de fundamentos
  const [currentType, setCurrentType] = useState<FundamentosType>('missao');
  const [answers, setAnswers] = useState<FundamentosAnswers>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<GeneratedOption[]>([]);
  
  // Estados separados para opções geradas de cada tipo
  const [missaoOptions, setMissaoOptions] = useState<GeneratedOption[]>([]);
  const [visaoOptions, setVisaoOptions] = useState<GeneratedOption[]>([]);
  const [valoresOptions, setValoresOptions] = useState<GeneratedOption[]>([]);
  
  const fundamentosQuestions = fundamentosQuestionsData as FundamentosQuestions;

  const setStepCompleted = (step: number, isCompleted: boolean) => {
    setCompletedSteps(prev => {
      const newSteps = [...prev];
      newSteps[step - 1] = isCompleted;
      return newSteps;
    });
  };

  // Load empresa data when component mounts
  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await empresaService.getEmpresa();
        if (data) {
          setEmpresa(data);
        }
      } catch (err) {
        console.error("Erro ao carregar empresa:", err);
        setError("Não foi possível carregar os dados da empresa. Por favor, tente novamente.");
        toastService.error("Erro ao carregar dados da empresa");
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.guid) {
      fetchEmpresa();
    }
  }, [user?.guid]);

  useEffect(() => {
    const isInitialsComplete =
      empresa.nome?.trim() &&
      empresa.anoInicioPlanejamento &&
      empresa.anosPlanejamento;

    setStepCompleted(1, !!isInitialsComplete);
  }, [empresa.nome, empresa.anoInicioPlanejamento, empresa.anosPlanejamento]);

  useEffect(() => {
    setStepCompleted(2, !!empresa.missao?.trim());
  }, [empresa.missao]);

  useEffect(() => {
    setStepCompleted(3, !!empresa.visao?.trim());
  }, [empresa.visao]);

  useEffect(() => {
    setStepCompleted(4, !!empresa.valores?.trim());
  }, [empresa.valores]);

  const handleOpenInfo = (section: 'missao' | 'visao' | 'valores') => {
    const examples = {
      missao: (
        <>
          <strong>Google</strong>: “Organizar as informações do mundo todo e torná-las acessíveis e úteis em caráter universal”.<br />
          <strong>Microsoft</strong>: “Ajudar as pessoas e empresas em todo o mundo a concretizarem todo o seu potencial”.<br />
          <strong>Vale</strong>: "Transformar recursos naturais em prosperidade e desenvolvimento sustentável."
        </>
      ),
      visao: (
        <>
          <strong>Petrobras</strong>: “Ser reconhecida como a melhor empresa de energia, garantindo o máximo valor para os seus acionistas e para a sociedade”.<br />
          <strong>Magazine Luiza</strong>: “Ser a maior e melhor plataforma digital de varejo do Brasil, conectando pessoas e negócios”.<br />
          <strong>Amazon</strong>: "Ser a empresa mais centrada no cliente do mundo, onde as pessoas podem encontrar e descobrir qualquer coisa que queiram comprar."
        </>
      ),
      valores: (
        <>
          <strong>Natura</strong>: “Bem-estar. Transparência nas relações. Compromisso com a verdade. Sustentabilidade como princípio”.<br />
          <strong>Apple</strong>: “Inovação contínua. Excelência em design e simplicidade. Foco na experiência do cliente".<br />
          <strong>Tesla</strong>: "Sustentabilidade ambiental. Inovação disruptiva. Ética e excelência no trabalho".
        </>
      )
    };

    setCurrentExamples({
      title: `Exemplos de ${section.charAt(0).toUpperCase() + section.slice(1)}`,
      content: examples[section]
    });
    setIsInfoModalOpen(true);
  };

  const handleChange = (field: keyof Empresa, value: any) => {
    setEmpresa((prev: Empresa) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await empresaService.save(empresa);
      toastService.success("Fundamentos salvos com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      setError("Erro ao salvar os dados da empresa. Por favor, tente novamente.");
      toastService.error("Erro ao salvar os fundamentos. Por favor, tente novamente.");
    }
  };

  const toggleSidebar = (open: boolean, type?: FundamentosType) => {
    setIsSidebarOpen(open);
    if (open && type) {
      setCurrentType(type);
      setAnswers({});
    }
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      const response = await empresaService.gerarFundamentos({
        tipo: currentType,
        respostas: answers
      });
      
      // Armazena as opções do tipo específico
      switch (currentType) {
        case 'missao':
          setMissaoOptions(response.opcoes);
          break;
        case 'visao':
          setVisaoOptions(response.opcoes);
          break;
        case 'valores':
          setValoresOptions(response.opcoes);
          break;
      }
      
      // Fecha o drawer
      setIsSidebarOpen(false);
      setAnswers({});
      
      // Define as opções para o modal
      setGeneratedOptions(response.opcoes);
      setIsSelectModalOpen(true);
      
      toastService.success("Sugestões geradas com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar fundamentos:", error);
      toastService.error("Erro ao gerar sugestões. Por favor, tente novamente.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectOption = (selectedText: string) => {
    handleChange(currentType, selectedText);
    setIsSelectModalOpen(false);
    toastService.success(`${currentType.charAt(0).toUpperCase() + currentType.slice(1)} atualizada com sucesso!`);
  };

  const openSelectModal = (type: FundamentosType) => {
    const optionsMap = {
      missao: missaoOptions,
      visao: visaoOptions,
      valores: valoresOptions
    };
    
    const options = optionsMap[type];
    
    if (options && options.length > 0) {
      setCurrentType(type);
      setGeneratedOptions(options);
      setIsSelectModalOpen(true);
    } else {
      toastService.warning("Por favor, gere as sugestões primeiro usando o botão ✨ no drawer.");
    }
  };

  // Show loading state when data is being fetched
  if (isLoading) {
    return <LoadingIndicator fullHeight message="Carregando dados da empresa..." />;
  }

  // Show error state only after loading is complete and there's an error
  if (error && !isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Typography color="error">{error}</Typography>
        <PrimaryButton onClick={() => globalThis.location.reload()}>
          Tentar Novamente
        </PrimaryButton>
      </Box>
    );
  }

  return (
    <>
      <InfoHeader
        title='Fundamentos'
        message='Discuta e descida os fundamentos que serão base para o planejamento estratégico da sua empresa.'
        isImportant
        url='https://youtu.be/EhKk33qiukI'
        modalTitle='Entenda a aba Diretrizes'
      />
      <Box sx={{ padding: { xs: 1, sm: 2, md: 4 }, maxWidth: 800, margin: 'auto' }}>
        <Box sx={{
          background: (theme) => theme.palette.primary.main,
          width: '100%',
          height: { xs: 160, sm: 200 },
          borderRadius: 2,
          marginBottom: 6
        }} >
          <Box sx={{ display: 'flex', padding: { xs: 2, sm: 4 }, gap: 1 }}>
            <img src="/logo512teste.png" alt="Logo" style={{ width: 44, height: 32, objectFit: 'cover' }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'semibold' }}>
              Fundamentos da Empresa
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: { xs: 1, sm: 4 },
            mt: 1,
            flexWrap: 'wrap',
            gap: { xs: 0, sm: 0 },
            '& hr': {
              flexGrow: 1,
              height: '1.5px',
              border: 'none',
              mt: 2
            }
          }}>
            <RadioForm label="Iniciais" isCompleted={completedSteps[0]} step={1} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[0] ? 'green' : 'white' }} />
            <RadioForm label="Missão" isCompleted={completedSteps[1]} step={2} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[1] ? 'green' : 'white' }} />
            <RadioForm label="Visão" isCompleted={completedSteps[2]} step={3} />
            <Divider style={{ maxWidth: '100px', backgroundColor: completedSteps[2] ? 'green' : 'white' }} />
            <RadioForm label="Valores" isCompleted={completedSteps[3]} step={4} />
          </Box>
        </Box>
        <Backdrop
          open={isSidebarOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer - 1,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
        />
        <Typography variant="h6" sx={{ mb: 3 }} gutterBottom>
          Informações Iniciais para o Planejamento
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Nome da Empresa"
              variant="outlined"
              color="secondary"
              value={empresa.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
            />
          </Grid>

          {/* Campos em linha abaixo - Lado a lado */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Ano de Início do Planejamento"
              variant="outlined"
              color="secondary"
              type="number"
              value={empresa.anoInicioPlanejamento}
              onChange={(e) => handleChange('anoInicioPlanejamento', Number(e.target.value))}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Quantidade em Anos de Planejamento"
              variant="outlined"
              color="secondary"
              type="number"
              value={empresa.anosPlanejamento}
              onChange={(e) => handleChange('anosPlanejamento', Number(e.target.value))}
            />
          </Grid>
        </Grid>

        {/* Modal com o video de ajuda */}
        <BasicModal
          title="Missão, Visão e Valores"
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          sizeContent='medium'
          tela='fundamentos'
        >
          <Box sx={{ position: 'relative', paddingTop: '56.2%' }}>
            <ReactPlayer
              url='https://www.youtube.com/watch?v=skg-29jvfsU'
              width='100%'
              height='100%'
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls
            />
          </Box>
        </BasicModal>

        <Box mt={4}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ m: 0 }} gutterBottom>
              Fundamentos
            </Typography>
            <IconButton
              onClick={() => setIsVideoModalOpen(true)}
              size="medium"
              sx={{ color: "black", cursor: 'pointer' }}
            >
              <InfoOutlined fontSize='small' />
            </IconButton>
          </Box>
          <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Missão"
              color='secondary'
              value={empresa.missao}
              onChange={(e) => handleChange('missao', e.target.value)}
            />
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => missaoOptions.length > 0 ? openSelectModal('missao') : toggleSidebar(true, 'missao')}
              sx={{ height: 57 }}
              title={missaoOptions.length > 0 ? "Selecionar opção gerada" : "Gerar com IA"}
            >
              <AutoAwesome fontSize='small' />
            </PrimaryButton>
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => handleOpenInfo('missao')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>
        <Box mt={4}>
          <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Visão"
              color='secondary'
              value={empresa.visao}
              onChange={(e) => handleChange('visao', e.target.value)}
            />
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => visaoOptions.length > 0 ? openSelectModal('visao') : toggleSidebar(true, 'visao')}
              sx={{ height: 57 }}
              title={visaoOptions.length > 0 ? "Selecionar opção gerada" : "Gerar com IA"}
            >
              <AutoAwesome fontSize='small' />
            </PrimaryButton>
            <PrimaryButton
              onClick={() => handleOpenInfo('visao')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>
        <Box mt={4}>
          <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Valores"
              color='secondary'
              value={empresa.valores}
              onChange={(e) => handleChange('valores', e.target.value)}
            />
            <PrimaryButton
              variant="contained"
              color="primary"
              onClick={() => valoresOptions.length > 0 ? openSelectModal('valores') : toggleSidebar(true, 'valores')}
              sx={{ height: 57 }}
              title={valoresOptions.length > 0 ? "Selecionar opção gerada" : "Gerar com IA"}
            >
              <AutoAwesome fontSize='small' />
            </PrimaryButton>
            <PrimaryButton
              onClick={() => handleOpenInfo('valores')}
              sx={{ height: 57 }}
            >
              <Info fontSize='medium' />
            </PrimaryButton>
          </Box>
        </Box>

        <Box mt={4} display="flex" justifyContent="center">
          <PrimaryButton onClick={handleSave}>
            Salvar Fundamentos
          </PrimaryButton>
        </Box>


        {/* Sidebar (Criar com IA) */}
        <Drawer
          anchor="right"
          open={isSidebarOpen}
          onClose={() => toggleSidebar(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '85%', sm: '50%', md: '33%' },
              padding: { xs: 2, sm: 4 },
            },
          }}
        >
          <Typography variant="h5">{fundamentosQuestions[currentType].title}</Typography>
          <Typography variant="body2" sx={{ marginBottom: 2, marginTop: 2, color: 'text.secondary' }}>
            {fundamentosQuestions[currentType].description}
          </Typography>
          
          {fundamentosQuestions[currentType].questions.map((question) => (
            <Box key={question.id} sx={{ marginBottom: 2 }}>
              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {question.text}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                color='secondary'
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                slotProps={{
                  input: {
                    sx: {
                      height: 48
                    }
                  }
                }}
              />
            </Box>
          ))}
          
          <Box display={"flex"} justifyContent={"end"} gap={2}>
            <SecondaryButton
              variant="contained"
              onClick={() => toggleSidebar(false)}
              sx={{ marginTop: 2 }}
            >
              Fechar
            </SecondaryButton>
            <PrimaryButton
              onClick={handleGenerate}
              disabled={isGenerating}
              sx={{ marginTop: 2 }}
            >
              {isGenerating ? 'Gerando...' : 'Gerar'}
            </PrimaryButton>
          </Box>
        </Drawer>
        <Dialog
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{currentExamples?.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {currentExamples?.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <SecondaryButton variant='contained' onClick={() => setIsInfoModalOpen(false)}>Fechar</SecondaryButton>
          </DialogActions>
        </Dialog>

        {/* Modal de Seleção de Fundamentos Gerados */}
        <SelectFundamentosModal
          open={isSelectModalOpen}
          onClose={() => setIsSelectModalOpen(false)}
          onSelect={handleSelectOption}
          options={generatedOptions}
          title={`Selecione sua ${currentType.charAt(0).toUpperCase() + currentType.slice(1)}`}
          isLoading={isGenerating}
        />
      </Box>
    </>
  );
};

export default Fundamentos;